// FRONT-1698: the six stops of the thinking slider (chat box, phone and
// Settings → Models). Pure, no React: every place the slider appears reads the
// same list, so a stop can never mean one level in the chat and another in
// Settings. The stored value is the person's `thinking` (null = Auto).

import type { ThinkingLevel } from '~/lib/types/llm';

export interface ThinkingStop {
  /** What is stored; null is Auto (no level sent, the model decides). */
  level: ThinkingLevel | null;
  label: string;
  /** One line for the hover hint and Settings. */
  hint: string;
  /** CSS colour token for the thumb, the filled track and the label. */
  color: string;
}

export const THINKING_STOPS: readonly ThinkingStop[] = [
  { level: 'off', label: 'Fast', hint: 'Answers straight away, with little or no thinking.', color: 'var(--thinking-1)' },
  { level: 'medium', label: 'Medium', hint: 'Thinks a little before answering.', color: 'var(--thinking-2)' },
  { level: 'high', label: 'High', hint: 'Thinks carefully before answering.', color: 'var(--thinking-3)' },
  { level: 'xhigh', label: 'Very high', hint: 'Thinks longer on hard problems. Slower and costs more.', color: 'var(--thinking-4)' },
  { level: 'max', label: 'Pro', hint: 'The deepest thinking the model offers. Slowest and most expensive.', color: 'var(--thinking-5)' },
  { level: null, label: 'Auto', hint: 'The model decides how much to think.', color: 'var(--thinking-auto)' },
];

export const AUTO_STOP = THINKING_STOPS.length - 1;

/** Very high and Pro: tiers only some models have; the rest think at High there. */
export const TOP_TIER_STOPS: ReadonlySet<number> = new Set([3, 4]);
export const HIGH_STOP = 2;

/**
 * Which stop a stored level sits on. Unset is Auto. "low" has no stop of its
 * own (it predates the slider) and shows on Fast, the nearest one; it stays
 * stored as "low" until the person moves the slider.
 */
export function stopIndexOf(level: ThinkingLevel | null | undefined): number {
  if (level == null) return AUTO_STOP;
  if (level === 'low') return 0;
  const i = THINKING_STOPS.findIndex((s) => s.level === level);
  return i === -1 ? AUTO_STOP : i;
}

/**
 * The level to save when the person lands on `stop`, or undefined when that
 * changes nothing. Compares LEVELS, not stops: a stored "low" sits on the Fast
 * stop, and choosing Fast must still turn it into "off".
 */
export function levelToSave(stop: number, stored: ThinkingLevel | null | undefined): ThinkingLevel | null | undefined {
  const level = THINKING_STOPS[stop]!.level;
  return level === (stored ?? null) ? undefined : level;
}

export function clampStop(index: number): number {
  return Math.min(AUTO_STOP, Math.max(0, Math.round(index)));
}

/** The stop nearest to a pointer at `fraction` (0 = left end, 1 = right end) of the track. */
export function stopAtFraction(fraction: number): number {
  return clampStop(fraction * AUTO_STOP);
}

/**
 * The stop marks, the fill and the thumb sit inset from the bar's ends by its
 * corner radius, so the end marks sit inside its rounded ends. One number for
 * drawing AND hit-testing: a click maps to the mark the person sees.
 */
export const MARK_INSET_PX = 3;

/** CSS position of a point along the bar (0 = Fast, 1 = Auto), matching {@link stopAtPointer}. */
export function along(fraction: number): string {
  return `calc(${MARK_INSET_PX}px + (100% - ${2 * MARK_INSET_PX}px) * ${fraction})`;
}

/** The stop whose drawn mark is nearest to a pointer at `x`, given the bar's left edge and width (px). */
export function stopAtPointer(x: number, barLeft: number, barWidth: number): number {
  const span = barWidth - 2 * MARK_INSET_PX;
  if (span <= 0) return AUTO_STOP;
  return stopAtFraction((x - barLeft - MARK_INSET_PX) / span);
}

/** Arrow keys, Home and End move the slider one stop or to an end; anything else is ignored. */
export function stopForKey(key: string, current: number): number | null {
  switch (key) {
    case 'ArrowRight':
    case 'ArrowUp':
      return clampStop(current + 1);
    case 'ArrowLeft':
    case 'ArrowDown':
      return clampStop(current - 1);
    case 'Home':
      return 0;
    case 'End':
      return AUTO_STOP;
    default:
      return null;
  }
}

/** The filled part of the track: the ramp up to the chosen stop's own colour. */
export function trackFill(index: number): string {
  if (index === AUTO_STOP) return 'var(--thinking-auto)';
  const colours = THINKING_STOPS.slice(0, index + 1).map((s) => s.color);
  return colours.length === 1 ? colours[0]! : `linear-gradient(90deg, ${colours.join(', ')})`;
}
